"use client";

import type React from "react";
import { useEffect, useState, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useDispatch, useSelector } from "react-redux";
import { incrementAlbumItemCount } from "../store/dreamworldSlice";

import { RootState } from "../store/store";
import dreamWorldProvider from "../DreamWorldProvider";
import type { Album } from "../types/library";

interface AddToAlbumModalProps {
  contentId: string;
  isOpen: boolean;
  onClose: () => void;
}

const AddToAlbumModal: React.FC<AddToAlbumModalProps> = ({
  contentId,
  isOpen,
  onClose,
}) => {
  const [albums, setAlbums] = useState<Album[]>([]);
  const [selectedAlbum, setSelectedAlbum] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const userGlobalData = useSelector(
    (state: RootState) => state.dreamworld.userGlobalData
  );
  const dispatch = useDispatch();

  useEffect(() => {
    if (isOpen && userGlobalData?.user?.uid) {
      const fetchAlbums = async () => {
        setLoading(true);
        try {
          const userAlbums = await dreamWorldProvider.getAlbums(
            userGlobalData.user.uid
          );
          setAlbums(userAlbums);
        } catch (err) {
          setError("Failed to fetch albums.");
          console.error(err);
        } finally {
          setLoading(false);
        }
      };
      fetchAlbums();
    }
  }, [isOpen, userGlobalData?.user?.uid]);

  const handleSave = async () => {
    if (!selectedAlbum || !contentId) return;

    setLoading(true);
    try {
      const content = await dreamWorldProvider.getLibraryItem(contentId);
      if (content) {
        await dreamWorldProvider.addContentToAlbum(content, selectedAlbum);
        dispatch(incrementAlbumItemCount(selectedAlbum));
      }
      onClose();
    } catch (err) {
      setError("Failed to add content to album.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[80]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-white"
                >
                  Add to Album
                </Dialog.Title>
                <div className="mt-4">
                  {loading && <p>Loading albums...</p>}
                  {error && <p className="text-red-500">{error}</p>}
                  {!loading && !error && (
                    <select
                      onChange={(e) => setSelectedAlbum(e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 mb-4 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Select an album</option>
                      {albums.map((album) => (
                        <option key={album.id} value={album.id}>
                          {album.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="mt-4 flex gap-3">
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-transparent bg-gray-700 px-4 py-2 text-sm font-medium text-white hover:bg-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-transparent bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 disabled:bg-gray-600"
                    onClick={handleSave}
                    disabled={!selectedAlbum || loading}
                  >
                    {loading ? "Saving..." : "Save"}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default AddToAlbumModal;
